import { useEffect } from 'react';
import { useCheckit } from '@/lib/checkit/CheckitAuthContext';

// Converts #rrggbb → "H S% L%" (the format checkit CSS variables expect).
function hexToHslTriple(hex) {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b); const min = Math.min(r, g, b);
  let hue = 0; const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) hue = ((g - b) / d) % 6;
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue *= 60; if (hue < 0) hue += 360;
  }
  return `${Math.round(hue)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Injects the CMS brand colours as CSS variables scoped to .checkit-scope,
// live-restyling the whole checkit site without a rebuild.
export default function CheckitThemeStyle() {
  const { site } = useCheckit();
  const t = site?.theme;
  // Once the fresh CMS theme is loaded, drop the synchronous index.html preload
  // so the server value is always authoritative (no stale cached colours).
  useEffect(() => {
    if (t) document.getElementById('checkit-theme-preload')?.remove();
  }, [t]);
  if (!t) return null;
  const vars = [
    ['--primary', hexToHslTriple(t.primary)],
    ['--secondary', hexToHslTriple(t.secondary)],
    ['--accent', hexToHslTriple(t.accent)],
  ].filter(([, v]) => v);
  if (vars.length === 0) return null;
  const css = `html .checkit-scope{${vars.map(([k, v]) => `${k}:${v};`).join('')}}`;
  return <style data-testid="checkit-theme-style">{css}</style>;
}
